import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { X, Download, FileText } from 'lucide-react';
import { ContractDocumentForClientDto, contractApi } from '../../services/contractService';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const Modal = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  max-width: 900px;
  width: 100%;
  height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-color);
  background: white;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 36px;
  height: 36px;
  background: var(--background-secondary);
  border-radius: var(--border-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--border-radius-sm);
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    color: var(--text-primary);
  }
`;

const Content = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const IframeContainer = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  flex-direction: column;
  gap: 12px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  color: var(--text-secondary);
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  text-align: center;
  padding: 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border-color);
  background: white;
  flex-shrink: 0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: none;

    &:hover {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--background-secondary);
    }
  `}
`;

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ContractDocumentViewModalProps {
  document: ContractDocumentForClientDto;
  onClose: () => void;
  onDownload: () => void;
  isDownloading?: boolean;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ContractDocumentViewModal: React.FC<ContractDocumentViewModalProps> = ({
  document,
  onClose,
  onDownload,
  isDownloading = false,
}) => {
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHtmlContent = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const content = await contractApi.getDocumentHtml(document.id);
      setHtmlContent(content);
    } catch (err) {
      console.error('Error loading document HTML:', err);
      setError('שגיאה בטעינת המסמך');
    } finally {
      setIsLoading(false);
    }
  }, [document.id]);

  useEffect(() => {
    loadHtmlContent();
  }, [loadHtmlContent]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal>
        <Header>
          <HeaderContent>
            <IconWrapper>
              <FileText size={18} />
            </IconWrapper>
            <Title>{document.documentTypeName}</Title>
          </HeaderContent>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          {isLoading && (
            <LoadingOverlay>
              <Spinner />
              <LoadingText>טוען מסמך...</LoadingText>
            </LoadingOverlay>
          )}

          {error && (
            <LoadingOverlay>
              <ErrorMessage>{error}</ErrorMessage>
            </LoadingOverlay>
          )}

          {!isLoading && !error && htmlContent && (
            <IframeContainer
              srcDoc={htmlContent}
              title={document.documentTypeName}
              sandbox="allow-same-origin"
            />
          )}
        </Content>

        <Footer>
          <Button variant="secondary" onClick={onClose}>
            סגור
          </Button>
          <Button
            variant="primary"
            onClick={onDownload}
            disabled={isDownloading}
          >
            <Download size={16} />
            {isDownloading ? 'מוריד...' : 'הורד PDF'}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ContractDocumentViewModal;
